import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  first,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DataSource } from '@angular/cdk/table';
import { CollectionViewer } from '@angular/cdk/collections';

import { AssististService } from '../assistist.service';
import { DocumentCategoryDTO } from '../DocumentCategory';

import { pageSizeOptions } from 'src/app/shared/utils/pagination';
import { LocalStorageService } from 'src/app/storage/local-storage.service';
import { SessionService } from 'src/app/session.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { UiService } from 'src/app/ui/ui.service';
import { FilterTableDialogComponent } from 'src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component';
import { AreYouSureDialogComponent } from 'src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component';
import { ElementLoaderDirective } from 'src/app/ui/element-loader/element-loader.directive';

@Component({
  selector: 'assistist-categories-list',
  templateUrl: './assistist-categories-list.component.html',
  styleUrls: ['./assistist-categories-list.component.scss'],
})
export class AssististCategoriesListComponent implements OnInit, AfterViewInit, OnDestroy {
  pageSizeOptions = pageSizeOptions;
  searchValueControl = new FormControl('');
  columns = [
    { code: 'id', name: 'ID' },
    { code: 'name', name: 'Category Name', filter: true, selected: true },
    { code: 'icon', name: 'Icon', filter: true, selected: true },
    { code: 'account_id', name: 'Account ID', filter: true, selected: false },
    { code: 'options' },
  ];
  displayedColumns = this.columns.map(c => c.code);
  perPage = 20;

  @ViewChild('categoriesTable') table!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(ElementLoaderDirective) elementLoader!: ElementLoaderDirective;

  hasWrite = false;
  hasDetail = false;
  hasRemove = false;
  private destroy$ = new Subject<void>();
  onFilterColumnChange = new Subject<void>();
  dataSource!: CategoriesDataSource;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private svc: AssististService,
    private session: SessionService,
    private notification: NotificationService,
    private ui: UiService,
    private cd: ChangeDetectorRef,
    private storage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.storage.pageSize || 20;
    
    // Subscribe to route parameter and data changes to handle navigation between tripist/explorist
    merge(
      this.activatedRoute.params,
      this.activatedRoute.data
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Update integration ID when route changes
      this.updateFromRouteParams();
    });
    
    // Also handle initial load
    this.updateFromRouteParams();
  }

  private updateFromRouteParams(): void {
    const perms = this.activatedRoute.snapshot.data.permissions!;
    this.hasWrite = perms.hasCategoriesWrite;
    this.hasDetail = perms.hasCategoriesDetail;
    this.hasRemove = perms.hasCategoriesRemove;
    
    // Restore saved filter state
    this.restoreFilterState();
    
    // Update displayed columns based on selected columns
    this.updateDisplayedColumns();
    
    // Trigger data reload if data source is already initialized
    if (this.dataSource && this.paginator) {
      this.loadPage().pipe(first()).subscribe();
    }
  }

  ngAfterViewInit(): void {
    this.dataSource = new CategoriesDataSource(this.svc, this.paginator, this.elementLoader);

    // Set default sort by name
    this.sort.sort({ id: 'name', start: 'asc', disableClear: false });

    // initial load
    this.loadPage().pipe(first()).subscribe();

    // react to sort, page, filter or search changes
    merge(
      this.sort.sortChange.pipe(tap(() => (this.paginator.pageIndex = 0))),
      this.paginator.page,
      this.onFilterColumnChange,
      this.searchValueControl.valueChanges.pipe(
        distinctUntilChanged(), 
        tap(() => {
          this.paginator.pageIndex = 0;
          this.saveFilterState();
        })
      ),
    )
      .pipe(
        debounceTime(300),
        switchMap(() => this.loadPage().pipe(first())),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get currentAccountID(): number {
    return this.session.currentAccountValue!.accountID;
  }

  private get currentIntegrationID(): number {
    // Get integration ID from route params
    return +this.activatedRoute.snapshot.params['integrationId'] || 1; // Default to 1 for now
  }

  loadPage() {
    const sortParam = this.sort.direction
      ? [`${this.sort.active}:${this.sort.direction}`]
      : [];
    return this.dataSource.loadCategories(this.currentAccountID, this.currentIntegrationID, {
      page: this.paginator.pageIndex,
      perPage: this.paginator.pageSize,
      keyword: this.searchValueControl.value ?? '',
      forCount: true,
      sort: sortParam,
      filter_columns: this.columns.filter(c => c.selected).map(c => c.code),
    });
  }

  create() {
    this.router.navigate(['create'], { relativeTo: this.activatedRoute.parent });
  }

  edit(cat: DocumentCategoryDTO) {
    this.router.navigate([cat.id], { relativeTo: this.activatedRoute.parent });
  }

  delete(cat: DocumentCategoryDTO) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Delete category " ${cat.name} "?`,
            warning: 'This cannot be undone!',
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(yes => {
        if (yes) {
          this.ui.waitForEvent(this.svc.deleteDocumentCategory(this.currentAccountID, this.currentIntegrationID, cat.id))
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              () => {
                this.notification.openSuccessSnackBar('Category deleted');
                this.loadPage().pipe(first()).subscribe();
              },
              () => this.notification.openErrorSnackBar('Error deleting category'),
            );
        }
      });
  }

  onColumnSelectionChange(event: MatCheckboxChange, column: any) {
    column.selected = !column.selected;
    this.updateDisplayedColumns();
    this.saveFilterState();
    this.onFilterColumnChange.next();
  }

  get filterableColumns() {
    return this.columns.filter(c => c.filter); // Only return filterable columns for the menu
  }

  get hasActiveFilters(): boolean {
    // Check if there are any active filters
    const hasSearchValue = !!(this.searchValueControl.value && this.searchValueControl.value.trim());
    
    // Check if any columns are deselected from their default state
    const hasModifiedColumns = this.columns.some(column => {
      // Default selected columns based on initial configuration
      const defaultSelected = column.code !== 'account_id' && column.code !== 'options' && column.code !== 'id';
      return column.selected !== defaultSelected;
    });

    return hasSearchValue || hasModifiedColumns;
  }

  resetAllFilters() {
    // Reset all filters and columns to their default state
    this.searchValueControl.setValue('');
    
    // Reset columns to their default state
    this.columns.forEach(column => {
      const defaultSelected = column.code !== 'account_id' && column.code !== 'options' && column.code !== 'id';
      column.selected = defaultSelected;
    });
    
    this.updateDisplayedColumns();
    this.saveFilterState();
  }

  private updateDisplayedColumns() {
    this.displayedColumns = this.columns
      .filter(c => c.selected !== false || c.code === 'options') // Always include options column
      .map(c => c.code);
  }

  onPageChange(e: PageEvent) {
    this.storage.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }

  private restoreFilterState() {
    const savedFilters = this.storage.getObject(
      'categoriesListFilters',
      this.currentAccountID,
      0 // Categories don't have integration ID, using 0
    );

    if (savedFilters) {
      this.searchValueControl.setValue(savedFilters.searchValue || '');
      
      // Restore column selections
      if (savedFilters.columns) {
        this.columns.forEach(column => {
          const savedColumn = savedFilters.columns.find((c: any) => c.code === column.code);
          if (savedColumn && savedColumn.selected !== undefined) {
            column.selected = savedColumn.selected;
          }
        });
      }
    }
  }

  private saveFilterState() {
    const filterState = {
      searchValue: this.searchValueControl.value,
      columns: this.columns.map(column => ({
        code: column.code,
        selected: column.selected
      }))
    };

    this.storage.setObject(
      'categoriesListFilters',
      this.currentAccountID,
      0, // Categories don't have integration ID, using 0
      filterState
    );
  }
}

/** DataSource for document categories */
export class CategoriesDataSource implements DataSource<DocumentCategoryDTO> {
  private subject = new BehaviorSubject<DocumentCategoryDTO[]>([]);

  constructor(
    private svc: AssististService,
    private paginator: MatPaginator,
    private loader: ElementLoaderDirective,
  ) {}

  connect(_collectionViewer: CollectionViewer): Observable<DocumentCategoryDTO[]> {
    return this.subject.asObservable();
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    this.subject.complete();
  }

  loadCategories(masterAccountID: number, integrationID: number, params: any) {
    return this.loader
      .waitForEvent(
        this.svc.listDocumentCategories(masterAccountID, integrationID, params).pipe(
          catchError(() => of({ count: 0, records: [] })),
        ),
      )
      .pipe(
        tap((resp: any) => {
          this.paginator.length = resp.count;
          this.subject.next(resp.records);
        }),
      );
  }
}
